import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useFetch } from "hooks/useFetch";
import { usePost } from "hooks/usePost";
import Button from "components/LoadingButton";
import Snackbar from "components/SnackBar";
import OrderComments from "components/OrderCard/OrderComments";
import OrderCardDialogTitle from "components/OrderCard/OrderCommentDialogTitle";
import { ORDERS_CHECK_IN } from "constants/services";
import Input from "components/TextFieldInput";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import {
  CheckedInWrapper,
  CheckedInForm,
  CheckedInData,
} from "styles/components/order-card";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const OrderCheckInDialog = ({ open, setOpen, order, refresh }) => {
  const salesOrder = order?.salesOrder;
  const lineItems = salesOrder?.lineItems;

  const [state, setState] = useState({
    partiallyStockReceived: false,
    completeStockReceived: false,
    comment: undefined,
    lineItems: [],
  });
  const { mutateAsync } = usePost();

  const { refetch: refetchCheckIns } = useFetch(
    ORDERS_CHECK_IN,
    `${ORDERS_CHECK_IN}/${order?.vendorName}/${order?.orderNumber}`,
    {
      enabled: false,
    }
  );

  const fetchCheckIns = async () => {
    const { data } = await refetchCheckIns();

    let latestLineItems = [];

    if (data?.length > 0) {
      latestLineItems = data[data?.length - 1]?.lineItems;
    }

    if (data) {
      setState({
        ...state,
        checkins: data,
        lineItems:
          latestLineItems?.length > 0
            ? latestLineItems
            : lineItems?.map((item) => ({ ...item, receivedQty: 0 })),
      });
    }
  };

  const addCheckIn = async () => {
    let stockStatus = "Complete";

    for (let i = 0; i < state?.lineItems?.length; i++) {
      const element = state?.lineItems[i];
      if (element?.receivedQty < element?.qty) {
        stockStatus = "Partial";
        break;
      }
    }

    const { data } = await mutateAsync({
      url: `${ORDERS_CHECK_IN}/${order?.vendorName}/${order?.orderNumber}`,
      data: {
        comment: state?.comment,
        // stockStatus: receivedQty >= actualQty ? "Complete" : "Partial",
        // stockStatus: "Partial",
        stockStatus,
        lineItems: state?.lineItems,
      },
    });

    if (data) {
      setState({
        ...state,
        checkins: data,
        snackbarMessageSeverity: "success",
        snackbarMessage: "Check-in added successfully !",
      });
      refresh();
    }
  };

  useState(() => {
    if (!state?.comments) fetchCheckIns();
  }, [state]);

  // const isCompleteStockReceived = !!state?.checkins?.find(
  //   (c) => c?.stockStatus === "Complete"
  // );

  // useEffect(() => {
  //   if (lineItems?.length > 0 && !state?.lineItems?.length) {
  //     setState({
  //       ...state,
  //       lineItems: lineItems.map((item) => ({ ...item, receivedQty: 0 })),
  //     });
  //   }
  // }, [lineItems]);

  return (
    <BootstrapDialog onClose={setOpen} open={open} fullWidth maxWidth="md">
      <OrderCardDialogTitle onClose={setOpen}>
        Order Check-in
      </OrderCardDialogTitle>
      <DialogContent dividers>
        {/* Line Items Table */}
        {state?.lineItems && state?.lineItems.length > 0 && (
          <div style={{ marginBottom: "24px" }}>
            <h3 style={{ marginBottom: "16px" }}>Line Items</h3>
            <TableContainer component={Paper} elevation={1}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: "bold" }}>Check</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Name</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>Qty</TableCell>
                    <TableCell style={{ fontWeight: "bold" }}>
                      Received Qty
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state?.lineItems?.map((lineItem, i) => (
                    <TableRow key={`line${i}`}>
                      <TableCell>
                        {lineItem?.receivedQty >= lineItem?.qty ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <HourglassEmptyIcon color="disabled" />
                        )}
                      </TableCell>
                      <TableCell>{lineItem.description}</TableCell>
                      <TableCell>{lineItem.qty || 0}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={lineItem.receivedQty || 0}
                          handleChange={(e) => {
                            const newValue = Number(e?.target?.value) || 0;
                            setState({
                              ...state,
                              lineItems: state?.lineItems?.map((item, j) => {
                                if (i !== j) return item;
                                return {
                                  ...item,
                                  receivedQty: newValue,
                                };
                              }),
                            });
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            label="Submit"
            variant="contained"
            // disabled={
            //   isCompleteStockReceived ||
            //   (state?.partiallyStockReceived
            //     ? !state?.comment?.trim()
            //     : !state?.completeStockReceived)
            // }
            onClick={addCheckIn}
          />
        </div>

        <CheckedInWrapper>
          <CheckedInForm></CheckedInForm>
        </CheckedInWrapper>

        <CheckedInData>
          <h3 className="title">Previous Check-Ins</h3>
          <OrderComments comments={state?.checkins} />
        </CheckedInData>
      </DialogContent>

      {!!state?.snackbarMessage && (
        <Snackbar
          message={state?.snackbarMessage}
          onClose={() =>
            setState({
              ...state,
              snackbarMessage: null,
              snackbarMessageSeverity: null,
            })
          }
          severity={state?.snackbarMessageSeverity}
        />
      )}
    </BootstrapDialog>
  );
};

OrderCheckInDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  refresh: PropTypes.func,
  order: PropTypes.shape({
    orderNumber: PropTypes.string,
    vendorName: PropTypes.string,
  }),
};

OrderCheckInDialog.defaultProps = {
  open: false,
  setOpen: () => {},
  order: { orderNumber: "", vendorName: "" },
  refresh: () => {},
};

export default OrderCheckInDialog;
